/**
 * Helpers for rendering the audit log's change records.
 *
 * The backend snapshots an entity as its columns plus its properties blob, and
 * diffs the two snapshots structurally. That produces three shapes this module
 * has to make readable:
 *
 *   - a scalar edit          { old, new }
 *   - a membership change    { added?, removed? }   (items joining/leaving a list)
 *   - a withheld value       "********:<fingerprint>"
 *
 * Paths are dotted and carry the blob prefix and list identities, e.g.
 * `properties.settings.max_file_size` or `allowed_thematics[t1].confidence_level`.
 *
 * Rows written before this shape existed used flat snake_case field names, and
 * both shapes are expected to appear side by side across the cutover.
 */

export const REDACTED_PREFIX = '********';

export type ScalarChange = { old?: unknown; new?: unknown };
export type MembershipChange = { added?: unknown[]; removed?: unknown[] };
export type AuditChange = ScalarChange | MembershipChange;

/** The blob key every new-shape path starts with; noise once it is on screen. */
const BLOB_PREFIX = 'properties.';

/** Split a dotted path, keeping `name[id]` segments intact. */
export const splitChangePath = (path: string): string[] => {
  const withoutBlobPrefix = path.startsWith(BLOB_PREFIX) ? path.slice(BLOB_PREFIX.length) : path;

  return withoutBlobPrefix.split('.').filter(Boolean);
};

/**
 * Turn a storage key into something readable.
 *
 * Blob keys are spelled however the feature that added them spelled them —
 * `greetingMessage`, `AllowedAnonymizeEntities` and `max_file_size` all appear —
 * so camelCase and snake_case have to come out the same way.
 */
export const humanizeKey = (key: string): string => {
  if (!key) return '';

  const identity = key.match(/\[(.*)\]$/);
  const name = identity ? key.slice(0, key.indexOf('[')) : key;

  const words = name
    .replace(/[_-]+/g, ' ')
    // camelCase and PascalCase boundaries
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    // runs of capitals followed by a word, e.g. "HTTPServer" -> "HTTP Server"
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) =>
      word === word.toUpperCase() ? word : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');

  return identity ? `${words} [${identity[1]}]` : words;
};

/** Render a change path as a readable trail, e.g. "Settings › Max File Size". */
export const formatChangePath = (path: string): string =>
  splitChangePath(path).map(humanizeKey).join(' › ') || humanizeKey(path);

export const isMembershipChange = (value: unknown): value is MembershipChange => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;

  const record = value as Record<string, unknown>;
  const hasMembership = 'added' in record || 'removed' in record;
  const hasScalar = 'old' in record || 'new' in record;

  return hasMembership && !hasScalar;
};

/** A value the backend withheld: present, changed-detectably, never revealed. */
export const isRedacted = (value: unknown): boolean =>
  typeof value === 'string' && value.startsWith(REDACTED_PREFIX);

/**
 * Describe a run of reads folded onto one row.
 *
 * Repeat reads of the same entity by the same user inside the dedup window are
 * counted rather than stored separately, so a row can stand for many accesses.
 */
export const formatOccurrences = (occurrences?: number | null): string | null => {
  if (!occurrences || occurrences <= 1) return null;
  return `${occurrences} times`;
};

export const hasOccurrences = (operation?: { occurrences?: number | null }): boolean =>
  Boolean(operation?.occurrences && operation.occurrences > 1);
